/**
 * GlossarySection — auto-generated glossary that lists every
 * dictionary acronym actually present in the rendered narrative.
 *
 * Mounted by SiteRagReportView immediately before ReferencesSection,
 * gated on `template.glossaryEnabled`. The default-on archetypes
 * (sovereign, utility, research, owner-operator, consultancy, energy-
 * major) carry the flag; default-off ones (hyperscaler, colocation,
 * IPP, financial-sponsor) suppress it.
 *
 * Returns null when the matched-acronym list is empty so a report
 * that happens not to use any dictionary terms doesn't render an
 * empty section heading.
 *
 * `data-section="glossary"` + `id="glossary"` so the print-mode
 * stylesheet's [data-section] rule pages it appropriately and the
 * TOC's #glossary anchor resolves.
 */
import React from 'react';
import { ACRONYMS } from '../../../config/acronyms';
import type { ReportTheme } from '../../../types/reportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import { reportSectionGapClass } from '../../../lib/reportSpacingTokens';
import {
  reportSectionPaddingClass,
  reportShellPaddingClass,
} from '../../../lib/reportSectionChrome';
import { REPORT_TEXT_PX } from '../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface GlossarySectionProps {
  /** Acronym keys to render. Typically extracted via
   *  extractAcronymsFromReport(report) at the call site. */
  acronyms: string[];
  /**
   * Active report theme. Optional for back-compat with the legacy
   * call site (SiteRagReportView passes none); `roleColor` is null-safe
   * and falls through to DEFAULT_THEME, so the legacy path keeps a
   * visually consistent neutral look while a themed caller picks up the
   * active preset's ink/rule tones.
   */
  theme?: ReportTheme;
  /**
   * SEC-INDENT — align the left content edge to the SAME single section
   * gutter every other section uses. Previously this section hardcoded
   * `px-[60px] pl-[70px]` (the legacy 40px confidential-rail + 30px
   * clearance), so it sat 10px further RIGHT than its neighbours once
   * the confidential rail was retired (`omitSectionSidebar` is true for
   * the whole report). Thread the same flags the rest of the report
   * passes so the gutter resolves through the shared helpers:
   *   omit=true              → uniform `lg:p-[60px]` (60px, matches neighbours)
   *   omit=false             → `lg:px-[60px] lg:pl-[70px]` (the standard rail gutter — preserved)
   *   shellPadding=false     → no self-padding (a padded page wrapper owns it, export/paged)
   */
  omitSectionSidebar?: boolean;
  /** When false, defer horizontal padding to a padded page wrapper. */
  shellPadding?: boolean;
}

export const GlossarySection: React.FC<GlossarySectionProps> = ({
  acronyms,
  theme,
  omitSectionSidebar = false,
  shellPadding = true,
}) => {
  // Filter to the entries that actually exist in the dictionary —
  // a stale acronym in the input list shouldn't render a blank row.
  const entries = acronyms
    .filter(a => ACRONYMS[a])
    .map(a => ({ key: a, ...ACRONYMS[a]! }));

  if (entries.length === 0) return null;

  // SEC-INDENT — resolve the horizontal gutter through the SAME helpers
  // every section shell uses so the left content edge aligns to the
  // single section gutter (no hardcoded `pl-[70px]` over-indent).
  const gutterClass = shellPadding
    ? omitSectionSidebar
      ? reportShellPaddingClass(true)
      : reportSectionPaddingClass(false)
    : '';
  // K2 — vertical rhythm reads the shared spacing tokens: section padding via
  // the density-aware helper (default `py-6`, `py-5` compact) + the flow gap
  // (`my-6`) so the glossary matches the tightened lattice instead of `my-8 py-8`.
  const sectionGapClass = reportSectionGapClass(theme?.density);

  return (
    <section
      className={`my-6 ${gutterClass} ${sectionGapClass} relative`}
      style={{ borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}
      data-section="glossary"
      data-testid="report-glossary"
    >
      <h2
        id="glossary"
        className="text-xl font-bold pb-2 mb-6"
        style={{
          scrollMarginTop: '80px',
          color: roleColor(theme, 'ink'),
          borderBottom: `2px solid ${roleColor(theme, 'rule')}`,
        }}
        data-heading="true"
      >
        Glossary
      </h2>
      <dl className="space-y-0">
        {entries.map((e, i) => (
          <div
            key={e.key}
            data-testid={`glossary-entry-${e.key}`}
            style={{
              paddingTop: i === 0 ? 0 : 10,
              paddingBottom: 10,
              borderTop: i === 0 ? 'none' : `1px solid ${roleColor(theme, 'hair')}`,
            }}
          >
            <dt className="font-bold">
              <span
                className="mr-2"
                style={{
                  fontFamily: MONO,
                  fontSize: REPORT_TEXT_PX.label,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: roleColor(theme, 'accent'),
                }}
              >
                {e.key}
              </span>
              <span className="font-normal" style={{ color: roleColor(theme, 'ink') }}>– {e.full}</span>
            </dt>
            <dd className="text-sm mt-0.5 leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
              {e.definition}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
